<script setup lang="ts">
import EventBus from '@legacy/js/service/EventBus';
import axios from 'axios';
import { FileText, LogIn, Pencil } from 'lucide';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { SphButton } from '@/components/base/button';
import { SphCheckbox } from '@/components/base/checkbox';
import { SphField } from '@/components/base/field';
import { SphInput } from '@/components/base/input';
import { SphSelect } from '@/components/base/select';
import FederationNewsForm from '@/components/federation/FederationNewsForm.vue';
import KindergartenEmailForm from '@/components/federation/KindergartenEmailForm.vue';
import { useI18n } from '@/composables/i18n';
import { useNotifications } from '@/composables/notifications';
import { useTableSort } from '@/composables/tableSort';
import TableSortHeader from './TableSortHeader.vue';

defineOptions({
    compatConfig: { MODE: 3 },
});

const props = defineProps<{
    federationId: number
    loginAllowed: boolean
    showSectionFilter?: boolean
    canSendEmail?: boolean
    provinces?: Record<string, string>
    loginBaseUrl: string
    profileBaseUrl: string
    editBaseUrl: string
    selectionSaveUrl: string
}>();

const { i18n } = useI18n();
const { notify } = useNotifications();

interface Section { id: number, name: string }
interface Kindergarten {
    id: number
    name: string
    kindergartenType: string | null
    street: string
    zip: string
    city: string
    province: string | null
    sections: Section[]
    executives: string[]
    unreadRegistrations: number
}

const kindergartens = ref<Kindergarten[]>([]);
const sections = ref<Section[]>([]);
const loading = ref(true);
const selected = ref<number[]>([]);
const filterSearch = ref('');
const filterSectionId = ref<string | undefined>(undefined);
const filterProvince = ref<string | undefined>(undefined);
const filterCity = ref<string | undefined>(undefined);

const sectionOptions = computed(() =>
    sections.value.map((s) => ({ value: String(s.id), label: s.name })),
);

const provinceOptions = computed(() => {
    const seen = new Set<string>();
    return kindergartens.value
        .filter((k) => k.province && props.provinces?.[k.province])
        .filter((k) => {
            if (seen.has(k.province!)) {
                return false;
            }
            seen.add(k.province!);
            return true;
        })
        .map((k) => ({ value: k.province!, label: props.provinces![k.province!] }))
        .sort((a, b) => a.label.localeCompare(b.label));
});

const cityOptions = computed(() => {
    const source = filterProvince.value
        ? kindergartens.value.filter((k) => k.province === filterProvince.value)
        : kindergartens.value;
    const seen = new Set<string>();
    return source
        .filter((k) => k.city)
        .filter((k) => {
            if (seen.has(k.city)) {
                return false;
            }
            seen.add(k.city);
            return true;
        })
        .map((k) => ({ value: k.city, label: k.city }))
        .sort((a, b) => a.label.localeCompare(b.label));
});

const filtered = computed(() =>
    kindergartens.value.filter((k) => {
        if (filterSearch.value && !k.name.toLowerCase().includes(filterSearch.value.toLowerCase())) {
            return false;
        }
        if (filterSectionId.value && !k.sections.some((s) => String(s.id) === filterSectionId.value)) {
            return false;
        }
        if (filterProvince.value && k.province !== filterProvince.value) {
            return false;
        }
        if (filterCity.value && k.city !== filterCity.value) {
            return false;
        }
        return true;
    }),
);

const { sortKey, sortDirection, toggleSort, sorted } = useTableSort(filtered, {
    name: (k) => k.name,
    kindergartenType: (k) => k.kindergartenType ? i18n.t(`kindergarten.${k.kindergartenType}`) : '',
    unreadRegistrations: (k) => k.unreadRegistrations,
    address: (k) => k.city,
    province: (k) => (props.provinces ?? {})[k.province ?? ''] ?? '',
    executives: (k) => k.executives.join(', '),
}, { storageKey: 'federation:kindergartenList:sort' });

const allSelected = computed(() =>
    filtered.value.length > 0 && filtered.value.every((k) => selected.value.includes(k.id)),
);

watch(filterProvince, () => {
    filterCity.value = undefined;
});

function loadSections() {
    axios.get(`/federations/${props.federationId}/sections`)
        .then((r) => {
            sections.value = r.data.data.map((d: any) => ({ id: d.id, name: d.name }));
        })
        .catch(() => notify.error('error.loadingData'));
}

const bulkAction = ref<string | undefined>(undefined);
const action = ref<'email' | 'news' | undefined>(undefined);
const noAction = computed(() => action.value === undefined);
const localStorageKey = `federation-${props.federationId}-kindergarten-selection`;

function saveSelectionToStorage() {
    localStorage.setItem(localStorageKey, JSON.stringify(selected.value));
}

function loadSelectionFromStorage() {
    const stored = localStorage.getItem(localStorageKey);
    if (!stored) {
        return;
    }
    try {
        const selectedIds = JSON.parse(stored) as number[];
        selected.value = selectedIds.filter((id) => kindergartens.value.some((k) => k.id === id));
    } catch {
        selected.value = [];
    }
}

function clearSelectionStorage() {
    localStorage.removeItem(localStorageKey);
}

watch(action, (newAction) => {
    const actionButtonsBlock = document.querySelector('.js-test-actionButtonsBlock');
    if (newAction) {
        window.location.hash = newAction;
        actionButtonsBlock?.classList.add('kgr-hidden');
    } else {
        history.replaceState(null, '', window.location.pathname + window.location.search);
        clearSelectionStorage();
        actionButtonsBlock?.classList.remove('kgr-hidden');
    }
});

watch(selected, saveSelectionToStorage, { deep: true });

let unsubscribeSection: (() => void) | undefined;

onMounted(() => {
    const hash = window.location.hash.slice(1);
    if ((hash === 'email' && props.canSendEmail) || hash === 'news') {
        action.value = hash;
    } else if (hash) {
        history.replaceState(null, '', window.location.pathname + window.location.search);
    }

    axios.get(`/federations/${props.federationId}/kindergartens`)
        .then((r) => {
            kindergartens.value = r.data.data.map((d: any) => ({
                id: d.id,
                name: d.name,
                kindergartenType: d.kindergartenType ?? null,
                street: d.street ?? '',
                zip: d.zip ?? '',
                city: d.city ?? '',
                province: d.province ?? null,
                sections: (d.sections ?? []).map((s: any) => ({ id: s.id, name: s.name })),
                executives: d.executives ?? [],
                unreadRegistrations: d.unreadRegistrations ?? 0,
            }));
            if (action.value) {
                loadSelectionFromStorage();
            }
        })
        .catch(() => notify.error('error.loadingData'))
        .finally(() => {
            loading.value = false;
        });
    if (props.showSectionFilter !== false) {
        loadSections();
    }
    unsubscribeSection = EventBus.$on('section:changed', (federationId: number) => {
        if (federationId === props.federationId) {
            loadSections();
        }
    });
});

onUnmounted(() => {
    unsubscribeSection?.();
});

function toggleSelect(id: number) {
    const idx = selected.value.indexOf(id);
    if (idx === -1) {
        selected.value.push(id);
    } else {
        selected.value.splice(idx, 1);
    }
}

function toggleAll() {
    if (allSelected.value) {
        selected.value = selected.value.filter((id) => !filtered.value.some((k) => k.id === id));
    } else {
        filtered.value.forEach((k) => {
            if (!selected.value.includes(k.id)) {
                selected.value.push(k.id);
            }
        });
    }
}

const bulkActionOptions = computed(() => {
    const options = [];
    if (props.canSendEmail) {
        options.push({ value: 'communicate', label: i18n.t('E-Mail senden') });
    }
    options.push({ value: 'news', label: i18n.t('Neuigkeit senden') });
    return options;
});

function handleBulkAction() {
    if (bulkAction.value === 'communicate') {
        action.value = 'email';
    }
    if (bulkAction.value === 'news') {
        action.value = 'news';
    }
    bulkAction.value = undefined;
}

function newsSent() {
    notify.success('Neuigkeit wurde gesendet.');
    action.value = undefined;
}

function emailsSent() {
    notify.success('E-Mails wurden verschickt.');
    action.value = undefined;
}

const printPage = () => window.print();

defineExpose({ kindergartens, sections, filtered, sorted, sortKey, sortDirection, toggleSort, selected, allSelected, provinceOptions, cityOptions, filterSearch, filterSectionId, filterProvince, filterCity });
</script>

<template>
    <div>
        <div class="kgr-row kgr-postfix kgr-postfix--between items-end">
            <div class="flex gap-4">
                <SphField
                    v-if="showSectionFilter !== false && noAction"
                    :label="i18n.t('Bereich')"
                >
                    <SphSelect
                        v-model="filterSectionId"
                        :options="sectionOptions"
                        :placeholder="i18n.t('Alle Bereiche')"
                        clearable
                    />
                </SphField>
                <SphField
                    v-if="noAction"
                    :label="i18n.t('Bundesland')"
                >
                    <SphSelect
                        v-model="filterProvince"
                        :options="provinceOptions"
                        :placeholder="i18n.t('Alle Bundesländer')"
                        clearable
                    />
                </SphField>
                <SphField
                    v-if="noAction"
                    :label="i18n.t('Ort')"
                >
                    <SphSelect
                        v-model="filterCity"
                        :options="cityOptions"
                        :placeholder="i18n.t('Alle Orte')"
                        clearable
                    />
                </SphField>
            </div>
            <SphInput
                v-if="noAction"
                v-model="filterSearch"
                :placeholder="i18n.t('Suche in dieser Liste ...')"
            />
        </div>

        <div
            v-if="loading"
            class="kgr-loading"
        ></div>

        <div v-else-if="action === 'email'">
            <KindergartenEmailForm
                v-model:selected-ids="selected"
                :federation-id="props.federationId"
                :all-kindergartens="kindergartens"
                @cancel="action = undefined"
                @done="emailsSent"
            />
        </div>

        <div v-else-if="action === 'news'">
            <FederationNewsForm
                :federation-id="props.federationId"
                target="kindergartens"
                :institutions="kindergartens"
                :preselected-ids="selected"
                @cancel="action = undefined"
                @saved="newsSent"
            />
        </div>

        <div
            v-else-if="filtered.length === 0"
            class="kgr-alert kgr-alert--info"
        >
            {{ i18n.t('Keine Kitas eingetragen') }}
        </div>

        <template v-else>
            <form
                method="post"
                :action="selectionSaveUrl"
                name="kindergartenAction"
            >
                <input
                    v-for="id in selected"
                    :key="id"
                    type="hidden"
                    name="kindergartens[]"
                    :value="id"
                />
                <input
                    type="hidden"
                    name="route"
                    value="kgr_federation_communicate"
                />

                <div class="kgr-row">
                    <table class="kgr-table kgr-table--striped kgr-table--block table-groups">
                        <thead>
                            <tr class="kgr-table__row">
                                <th class="kgr-table__cell kgr-table__cell--headline">
                                    <SphCheckbox
                                        :model-value="allSelected"
                                        @update:model-value="toggleAll"
                                    />
                                </th>
                                <TableSortHeader
                                    :label="i18n.t('Name der Einrichtung')"
                                    sort-key="name"
                                    :active-key="sortKey"
                                    :direction="sortDirection"
                                    @sort="toggleSort"
                                />
                                <TableSortHeader
                                    :label="i18n.t('Einrichtungsart')"
                                    sort-key="kindergartenType"
                                    :active-key="sortKey"
                                    :direction="sortDirection"
                                    @sort="toggleSort"
                                />
                                <TableSortHeader
                                    :label="i18n.t('Ungelesene Online-Anmeldungen')"
                                    sort-key="unreadRegistrations"
                                    :active-key="sortKey"
                                    :direction="sortDirection"
                                    @sort="toggleSort"
                                />
                                <TableSortHeader
                                    :label="i18n.t('Adresse')"
                                    sort-key="address"
                                    :active-key="sortKey"
                                    :direction="sortDirection"
                                    @sort="toggleSort"
                                />
                                <TableSortHeader
                                    :label="i18n.t('Bundesland')"
                                    sort-key="province"
                                    :active-key="sortKey"
                                    :direction="sortDirection"
                                    @sort="toggleSort"
                                />
                                <TableSortHeader
                                    :label="i18n.t('Leitung')"
                                    sort-key="executives"
                                    :active-key="sortKey"
                                    :direction="sortDirection"
                                    @sort="toggleSort"
                                />
                                <th class="kgr-table__cell kgr-table__cell--headline kgr-table__cell--actions">
                                    {{ i18n.t('options') }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="kindergarten in sorted"
                                :key="kindergarten.id"
                                class="kgr-table__row"
                            >
                                <td class="kgr-table__cell">
                                    <SphCheckbox
                                        :model-value="selected.includes(kindergarten.id)"
                                        @update:model-value="() => toggleSelect(kindergarten.id)"
                                    />
                                </td>
                                <td class="kgr-table__cell">
                                    <a
                                        v-if="loginAllowed"
                                        :href="`${loginBaseUrl}/${kindergarten.id}`"
                                        class="kgr-link kgr-link--unobtrusive"
                                    >{{ kindergarten.name }}</a>
                                    <template v-else>
                                        {{ kindergarten.name }}
                                    </template>
                                </td>
                                <td class="kgr-table__cell">
                                    {{ kindergarten.kindergartenType ? i18n.t(`kindergarten.${kindergarten.kindergartenType}`) : '' }}
                                </td>
                                <td class="kgr-table__cell kgr-table__cell--centered">
                                    <div
                                        v-if="kindergarten.unreadRegistrations > 0"
                                        class="kgr-badge kgr-badge--highlight"
                                    >
                                        {{ kindergarten.unreadRegistrations }}
                                    </div>
                                </td>
                                <td class="kgr-table__cell">
                                    <div class="kgr-text kgr-text--truncated kgr-text--noWrap">
                                        {{ kindergarten.street }}
                                    </div>
                                    <div class="kgr-text kgr-text--truncated kgr-text--noWrap">
                                        {{ kindergarten.zip }} {{ kindergarten.city }}
                                    </div>
                                </td>
                                <td class="kgr-table__cell">
                                    {{ (provinces ?? {})[kindergarten.province ?? ''] || '-' }}
                                </td>
                                <td class="kgr-table__cell">
                                    {{ kindergarten.executives.join(', ') }}
                                </td>
                                <td class="kgr-table__cell kgr-table__cell--actions">
                                    <template v-if="loginAllowed">
                                        <SphButton
                                            :icon="FileText"
                                            as="a"
                                            :href="`${profileBaseUrl}/${kindergarten.id}`"
                                            :title="i18n.t('Kurzprofil')"
                                            variant="ghost"
                                            size="md"
                                            class="kgr-table__action"
                                        />
                                        <SphButton
                                            :icon="Pencil"
                                            as="a"
                                            :href="`${editBaseUrl}/${kindergarten.id}/edit`"
                                            :title="i18n.t('edit')"
                                            variant="ghost"
                                            size="md"
                                            class="kgr-table__action"
                                        />
                                        <SphButton
                                            :icon="LogIn"
                                            as="a"
                                            :href="`${loginBaseUrl}/${kindergarten.id}`"
                                            :title="i18n.t('login')"
                                            variant="ghost"
                                            size="md"
                                            class="kgr-table__action"
                                        />
                                    </template>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="kgr-postfix kgr-postfix--between">
                    <div class="kgr-postfix__fixed">
                        <SphField :label="i18n.t('Aktion für Auswahl')">
                            <SphSelect
                                v-model="bulkAction"
                                :disabled="selected.length === 0"
                                :options="bulkActionOptions"
                                :placeholder="i18n.t('bitte wählen')"
                                @update:model-value="handleBulkAction"
                            />
                        </SphField>
                    </div>
                    <div class="kgr-postfix__fixed">
                        <SphButton
                            :label="i18n.t('print')"
                            variant="primary"
                            class="kgr-hidden-print"
                            @click="printPage"
                        />
                    </div>
                </div>
            </form>
        </template>
    </div>
</template>
